"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WelcomeEmailSettingsSchema = exports.APosStorefrontSettingsSchema = exports.LocationPaymentOptionsSchema = exports.ONLINE_PROCESSOR_KINDS = exports.APosStorefrontHomepageSchema = exports.APosStorefrontTogglesSchema = exports.APosStorefrontBrandingSchema = void 0;
const zod_1 = require("zod");
const timestamps_schema_1 = require("../../common/timestamps.schema");
const id_schema_1 = require("../../common/id.schema");
exports.APosStorefrontBrandingSchema = zod_1.z.object({
    displayName: zod_1.z.string().max(200).optional(),
    tagline: zod_1.z.string().max(300).optional(),
    aboutStory: zod_1.z.string().max(5000).optional(),
    logoUrl: zod_1.z.string().optional(),
    faviconUrl: zod_1.z.string().optional(),
    primaryColor: zod_1.z.string().optional(),
    accentColor: zod_1.z.string().optional(),
    backgroundColor: zod_1.z.string().optional(),
    inkColor: zod_1.z.string().optional(),
    displayFont: zod_1.z.string().optional(),
    bodyFont: zod_1.z.string().optional(),
}).default({});
exports.APosStorefrontTogglesSchema = zod_1.z.object({
    published: zod_1.z.boolean().default(false),
    guestCheckoutAllowed: zod_1.z.boolean().default(true),
    showOutOfStock: zod_1.z.boolean().default(true),
    taxIncluded: zod_1.z.boolean().default(false),
    poweredByVisible: zod_1.z.boolean().default(true),
    // SP6: how many category levels the storefront nav shows (1=top only, 2=+subs,
    // 3=three levels…). Undefined = show all levels.
    categoryNavDepth: zod_1.z.number().int().min(1).max(6).optional(),
}).default({});
/**
 * Structured per-section controls for the storefront home page. Every section has
 * a `visible` switch plus optional content overrides (the storefront falls back to
 * sensible defaults when a field is empty). Additive — the legacy `sections` /
 * `announcementBar` / `footer` fields are kept for back-compat.
 */
exports.APosStorefrontHomepageSchema = zod_1.z.object({
    // Legacy / back-compat (do not remove).
    sections: zod_1.z.array(zod_1.z.any()).default([]),
    announcementBar: zod_1.z.string().optional(),
    footer: zod_1.z.string().optional(),
    // Structured section controls.
    announcement: zod_1.z.object({
        visible: zod_1.z.boolean().default(true),
        text: zod_1.z.string().max(300).optional(),
    }).default({}),
    hero: zod_1.z.object({
        visible: zod_1.z.boolean().default(true),
        headline: zod_1.z.string().max(300).optional(),
        subtext: zod_1.z.string().max(500).optional(),
        imageUrl: zod_1.z.string().optional(),
        ctaLabel: zod_1.z.string().max(60).optional(),
    }).default({}),
    infoStrip: zod_1.z.object({
        visible: zod_1.z.boolean().default(true),
    }).default({}),
    categories: zod_1.z.object({
        visible: zod_1.z.boolean().default(true),
        title: zod_1.z.string().max(120).optional(),
    }).default({}),
    featured: zod_1.z.object({
        visible: zod_1.z.boolean().default(true),
        title: zod_1.z.string().max(120).optional(),
    }).default({}),
    story: zod_1.z.object({
        visible: zod_1.z.boolean().default(true),
        title: zod_1.z.string().max(160).optional(),
        body: zod_1.z.string().max(5000).optional(),
        imageUrl: zod_1.z.string().optional(),
    }).default({}),
    newsletter: zod_1.z.object({
        visible: zod_1.z.boolean().default(true),
        title: zod_1.z.string().max(160).optional(),
        subtext: zod_1.z.string().max(300).optional(),
    }).default({}),
    // Optional render order of the section keys (announcement is fixed at top).
    sectionOrder: zod_1.z.array(zod_1.z.string()).default([]),
}).default({});
// Per-location online payment options (Dejavoo online checkout). Stored as a
// per-location map inside checkout.locationPayments[locationId]. Additive.
// Which online card processor a location uses at checkout (ecommerce-service
// resolveOnlineProcessor): 'magtek' chosen AND a MagTek processor bound → MagTek;
// otherwise Dejavoo when a Dejavoo TPN is bound, else MagTek when one is bound.
// So absent ≠ always 'dejavoo': a MagTek-only location charges through MagTek.
exports.ONLINE_PROCESSOR_KINDS = ['dejavoo', 'magtek'];
exports.LocationPaymentOptionsSchema = zod_1.z.object({
    onlinePayment: zod_1.z.boolean().default(false), // Dejavoo online charge at checkout
    cashOnDelivery: zod_1.z.boolean().default(true), // recorded only; collected in person at hand-off
    onlineProcessor: zod_1.z.enum(exports.ONLINE_PROCESSOR_KINDS).optional(),
});
exports.APosStorefrontSettingsSchema = zod_1.z.object({
    tenantId: id_schema_1.TenantIdSchema,
    branding: exports.APosStorefrontBrandingSchema,
    toggles: exports.APosStorefrontTogglesSchema,
    homepage: exports.APosStorefrontHomepageSchema,
    legal: zod_1.z.object({
        termsUrl: zod_1.z.string().optional(),
        privacyUrl: zod_1.z.string().optional(),
        returnsUrl: zod_1.z.string().optional(),
        shippingUrl: zod_1.z.string().optional(),
    }).default({}),
    emails: zod_1.z.object({
        fromName: zod_1.z.string().optional(),
        replyTo: zod_1.z.string().optional(),
    }).default({}),
    seo: zod_1.z.object({
        metaTitle: zod_1.z.string().optional(),
        metaDescription: zod_1.z.string().optional(),
        ogImageUrl: zod_1.z.string().optional(),
        ga4Id: zod_1.z.string().optional(),
        metaPixelId: zod_1.z.string().optional(),
    }).default({}),
    // The tenant's chosen "main online location": the storefront lists catalog +
    // online pricing/stock from this location. Optional until the tenant picks one.
    fulfillmentLocationId: zod_1.z.string().optional(),
    // P3 (fulfillment redesign §5.3/6.3): tenant-level order-routing mode. Additive +
    // optional so existing rows/fixtures parse unchanged and inferred fields stay
    // optional (NOT `.default()` — that makes them required in the inferred type and
    // breaks existing settings literals; same pattern as location `afterCutoffMode`).
    // Consumers treat a missing `routingMode` as 'nearest' (today's behavior):
    //  - 'single'  — route only to `defaultLocationId`'s store (no fan-out to others).
    //  - 'nearest' — current nearest-in-zone algorithm (unchanged).
    //  - 'zone'    — stores whose delivery zone covers the address, ordered by `priority`.
    // `priority` (locationIds, earlier = higher) breaks ties in nearest/zone and orders
    // zone matches; `defaultLocationId` names the store used by 'single'. Enforced in
    // ecommerce-service resolve-store.ts + candidates.ts/fulfillment-router.ts.
    fulfillment: zod_1.z.object({
        routingMode: zod_1.z.enum(['single', 'nearest', 'zone']).optional(),
        defaultLocationId: zod_1.z.string().nullable().optional(),
        priority: zod_1.z.array(zod_1.z.string()).optional(),
        // Per-channel routing (separate delivery vs shipping logic). Additive; the flat
        // fields above mirror `delivery` for the current backend. Enforced at checkout binding.
        delivery: zod_1.z.object({
            routingMode: zod_1.z.enum(['single', 'nearest', 'zone']).optional(),
            defaultLocationId: zod_1.z.string().nullable().optional(),
            priority: zod_1.z.array(zod_1.z.string()).optional(),
        }).optional(),
        shipping: zod_1.z.object({
            routingMode: zod_1.z.enum(['single', 'nearest', 'zone']).optional(),
            defaultLocationId: zod_1.z.string().nullable().optional(),
            priority: zod_1.z.array(zod_1.z.string()).optional(),
        }).optional(),
    }).optional(),
    // Checkout policy (tenant-wide + per-location). Additive; defaults keep existing rows valid.
    checkout: zod_1.z.object({
        ageVerification: zod_1.z.object({
            enabled: zod_1.z.boolean().default(false),
            minimumAge: zod_1.z.number().int().min(18).max(21).default(21),
        }).default({}),
        // Per-location online payment options (Dejavoo online charge + cash on
        // delivery). Optional map; key = locationId. Additive.
        locationPayments: zod_1.z.record(zod_1.z.string(), exports.LocationPaymentOptionsSchema).optional(),
    }).default({}),
    // Welcome Email (Phase 5 — admin-configurable per store).
    // Optional so existing rows without this field still validate (additive).
    welcomeEmail: zod_1.z.object({
        enabled: zod_1.z.boolean().default(true),
        subject: zod_1.z.string().max(200).default('Welcome to {storeName}!'),
        body: zod_1.z.string().max(10000).default('Hi {customerName},\n\nWelcome to {storeName}! We\'re so glad you\'re here.\n\nExplore our products and enjoy a seamless shopping experience.\n\nThanks for joining us!\n\nThe {storeName} Team'),
    }).default({}),
}).merge(timestamps_schema_1.TimestampsSchema);
/** Nested schema for the welcome email config — useful when extending independently. */
exports.WelcomeEmailSettingsSchema = exports.APosStorefrontSettingsSchema.shape.welcomeEmail;
//# sourceMappingURL=storefront-settings.schema.js.map